// The Process dialog (Fernando, 2026-10-05) — replaces window.confirm for the ONE Process path
// (processOrders.ts: the Orders list's "Process orders" and the order page's "Process order").
//   confirm  the usual text + "I checked the boxes: no item and no delivery note is left over" — Process
//            stays disabled until it is ticked (two customers got a parcel without an item that the log
//            shows was scanned: it was set aside and forgotten)
//   held     the server refused because a P&P round is not fully packed (pp_tracking.round_guard) — says
//            which orders are still to pack / which labels were not scanned; a manager or admin may
//            "Process anyway" with a note
// Rendered imperatively into its own root, so the two callers stay one-line awaits. Texts in the packer's
// language (packingText.ts; ET/RU by Claude, to be checked by Fernando).
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { packingText } from './packingText';

type Tx = ReturnType<typeof packingText>;
export interface HeldRound { round_id: number; text: string; still_to_pack: string[]; label_not_scanned: string[]; orders_held: string[]; order_ids?: string[] }
type Answer = { ok: boolean; force?: boolean; note?: string };

function Dialog({ kind, tx, title, text, rounds, processed, canForce, done }: {
  kind: 'confirm' | 'held'; tx: Tx; title: string; text?: string; rounds?: HeldRound[]; processed?: number;
  canForce?: boolean; done: (a: Answer) => void;
}) {
  const [checked, setChecked] = useState(false);
  const [forcing, setForcing] = useState(false);
  const [note, setNote] = useState('');
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-3" onClick={() => done({ ok: false })}>
      <div className="bg-[#1a1d27] border border-gray-700 rounded-xl w-full max-w-lg p-5 space-y-4 text-gray-200 max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className={`text-lg font-semibold ${kind === 'held' ? 'text-amber-300' : 'text-white'}`}>{title}</div>
        {kind === 'confirm' && (
          <>
            <div className="text-sm text-gray-300 whitespace-pre-line">{text}</div>
            <label className={`flex items-start gap-3 rounded-lg border-2 px-3 py-3 cursor-pointer ${checked ? 'border-green-700 bg-green-950/30' : 'border-amber-600 bg-amber-950/30'}`}>
              <input type="checkbox" className="mt-1 w-5 h-5 accent-green-500" checked={checked} onChange={e => setChecked(e.target.checked)} autoFocus />
              <span>
                <span className="font-semibold text-white">{tx.boxCheck}</span>
                <span className="block text-xs text-gray-400 mt-1">{tx.boxCheckHint}</span>
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => done({ ok: false })} className="px-4 py-2 text-sm text-gray-400 hover:text-white">{tx.cancel}</button>
              <button disabled={!checked} onClick={() => done({ ok: true })}
                className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-white">{tx.process}</button>
            </div>
          </>
        )}
        {kind === 'held' && (
          <>
            <div className="text-sm text-gray-300">{tx.roundHeldBody}</div>
            {(rounds || []).map(r => (
              <div key={r.round_id} className="text-sm rounded-lg border border-amber-800/60 bg-amber-950/30 px-3 py-2 space-y-1">
                <div className="font-semibold text-amber-200">{tx.roundWord} {r.round_id}</div>
                {r.still_to_pack.length > 0 && <div>{tx.stillToPack} ({r.still_to_pack.length}): <span className="text-white">{r.still_to_pack.join(', ')}</span></div>}
                {r.label_not_scanned.length > 0 && <div>{tx.labelNotScanned} ({r.label_not_scanned.length}): <span className="text-white">{r.label_not_scanned.join(', ')}</span>
                  <span className="block text-xs text-gray-400">{tx.labelNotScannedHint}</span></div>}
                <div className="text-xs text-gray-400">{tx.notProcessedNow}: {r.orders_held.join(', ')}</div>
              </div>
            ))}
            {!!processed && <div className="text-sm text-green-300">{tx.processedOutside(processed)}</div>}
            <div className="text-xs text-gray-500">{tx.waitHint}</div>
            {canForce && forcing && (
              <div className="space-y-2">
                <div className="text-xs text-gray-400">{tx.forceHint}</div>
                <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} autoFocus
                  className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2" />
              </div>
            )}
            <div className="flex justify-end gap-2">
              {canForce && !forcing && <button onClick={() => setForcing(true)} className="px-4 py-2 text-sm text-gray-400 hover:text-amber-300">{tx.forceButton}…</button>}
              {canForce && forcing && <button disabled={!note.trim()} onClick={() => done({ ok: true, force: true, note: note.trim() })}
                className="px-4 py-2 rounded-lg bg-amber-700 hover:bg-amber-600 disabled:opacity-40 text-sm text-white">{tx.forceButton}</button>}
              <button onClick={() => done({ ok: false })} className="px-5 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-medium text-white">{tx.ok}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Show the dialog; resolves with the answer and removes itself. */
export function showProcessDialog(props: Omit<React.ComponentProps<typeof Dialog>, 'done'>): Promise<Answer> {
  return new Promise(resolve => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = (a: Answer) => { root.unmount(); host.remove(); resolve(a); };
    root.render(<Dialog {...props} done={done} />);
  });
}
